import React, { useState, useEffect, useCallback } from 'react';
import { screensAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { RefreshCw, Plus, Trash2, ScanSearch, Pencil, X } from 'lucide-react';
import '../Admin/AdminDashboard.css';

const STATUSES = [
  'not_started', 'in_preparation', 'delivered', 'replied', 'call_held', 'declined', 'converted',
];
const VERTICALS = ['maritime', 'superyacht', 'electronics', 'pharma', 'medtech', 'aerospace', 'other'];
const TIERS = ['A', 'B', 'C', 'D'];

const emptyForm = () => ({
  screenId: '',
  customerName: '',
  siteOrVessel: '',
  vertical: 'maritime',
  accountTier: 'B',
  stExclusionCleared: false,
  status: 'not_started',
  honestExclusions: '',
  nextAsk: '20-minute call on highest-value candidate',
});

const AdminScreens = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await screensAPI.getAll({ limit: 100, status: statusFilter || undefined });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load screens');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      screenId: row.screenId || '',
      customerName: row.customerName || '',
      siteOrVessel: row.siteOrVessel || '',
      vertical: row.vertical || 'maritime',
      accountTier: row.accountTier || 'B',
      stExclusionCleared: Boolean(row.stExclusionCleared),
      status: row.status || 'not_started',
      honestExclusions: row.honestExclusions || '',
      nextAsk: row.nextAsk || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await screensAPI.update(editingId, form);
        toast.success('Screen updated');
      } else {
        await screensAPI.create(form);
        toast.success('Screen created');
      }
      setShowForm(false);
      load();
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this obsolescence screen?')) return;
    try {
      await screensAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ScanSearch size={28} color="#14b8a6" /> Obsolescence Screens
            </h1>
            <p style={{ color: '#a1a1aa' }}>
              Free sales instrument (Roadmap Appendix A).
              {total ? ` ${total} screens.` : ' Empty — log Malta ICP screens here (SA owns final copy).'}
            </p>
          </div>
          <div className="admin-header-right">
            <button onClick={openCreate} style={btn('#14b8a6')}><Plus size={15} /> New screen</button>
            <button className="btn-secondary" onClick={load}><RefreshCw size={16} /></button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <Chip active={!statusFilter} onClick={() => setStatusFilter('')} label="All" color="#14b8a6" />
          {STATUSES.map((s) => (
            <Chip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} label={s} color="#14b8a6" />
          ))}
        </div>

        {showForm && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: '#fff' }}>{editingId ? 'Edit screen' : 'New obsolescence screen'}</h3>
              <button onClick={() => setShowForm(false)} style={iconBtn}><X size={16} /></button>
            </div>
            <div style={grid}>
              <label style={field}><span style={muted}>Screen ID</span>
                <input value={form.screenId} onChange={(e) => setForm((p) => ({ ...p, screenId: e.target.value }))} placeholder="SCR-2026-001" style={input} /></label>
              <label style={field}><span style={muted}>Customer name</span>
                <input value={form.customerName} onChange={(e) => setForm((p) => ({ ...p, customerName: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Site / vessel</span>
                <input value={form.siteOrVessel} onChange={(e) => setForm((p) => ({ ...p, siteOrVessel: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Vertical</span>
                <select value={form.vertical} onChange={(e) => setForm((p) => ({ ...p, vertical: e.target.value }))} style={input}>
                  {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select></label>
              <label style={field}><span style={muted}>Account tier</span>
                <select value={form.accountTier} onChange={(e) => setForm((p) => ({ ...p, accountTier: e.target.value }))} style={input}>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></label>
              <label style={field}><span style={muted}>Status</span>
                <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} style={input}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></label>
              <label style={{ ...field, gridColumn: '1 / -1' }}><span style={muted}>Honest exclusions</span>
                <textarea value={form.honestExclusions} onChange={(e) => setForm((p) => ({ ...p, honestExclusions: e.target.value }))} rows={2} style={input} /></label>
              <label style={{ ...field, gridColumn: '1 / -1' }}><span style={muted}>Next ask</span>
                <input value={form.nextAsk} onChange={(e) => setForm((p) => ({ ...p, nextAsk: e.target.value }))} style={input} /></label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#a1a1aa', fontSize: 13 }}>
                <input type="checkbox" checked={form.stExclusionCleared} onChange={(e) => setForm((p) => ({ ...p, stExclusionCleared: e.target.checked }))} />
                ST exclusion cleared
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={handleSave} style={btn('#14b8a6')}>Save</button>
              <button onClick={() => setShowForm(false)} style={ghost}>Cancel</button>
            </div>
          </div>
        )}

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr style={thead}>
                {['Screen', 'Customer', 'Vertical', 'Tier', 'Status', 'ST cleared', 'Created', ''].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={empty}>Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} style={empty}>
                  No obsolescence screens yet. Start with Tier A/B maritime or electronics (excl. ST) accounts.
                </td></tr>
              ) : items.map((row) => (
                <tr key={row._id} style={rowStyle}>
                  <td style={td}><strong>{row.screenId || row._id.slice(-6)}</strong></td>
                  <td style={td}>
                    <div>{row.customerName || '—'}</div>
                    <div style={{ color: '#71717a', fontSize: 12 }}>{row.siteOrVessel || ''}</div>
                  </td>
                  <td style={td}>{row.vertical || '—'}</td>
                  <td style={td}>{row.accountTier || '—'}</td>
                  <td style={td}><span style={pill('#14b8a6')}>{row.status}</span></td>
                  <td style={td}>{row.stExclusionCleared ? 'Yes' : 'No'}</td>
                  <td style={td}>{fmt(row.createdAt)}</td>
                  <td style={td}>
                    <button onClick={() => openEdit(row)} style={iconBtn}><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(row._id)} style={iconBtn}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

function Chip({ active, onClick, label, color }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '6px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
      border: active ? `2px solid ${color}` : '1px solid #27272a',
      background: active ? `${color}20` : '#18181b',
      color: active ? color : '#a1a1aa',
      fontWeight: active ? 600 : 400,
    }}>{label}</button>
  );
}

const btn = (c) => ({ background: c, border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 });
const ghost = { background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' };
const iconBtn = { background: 'transparent', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: 6 };
const card = { background: '#18181b', borderRadius: 12, border: '1px solid #27272a', padding: 24, marginBottom: 24 };
const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };
const muted = { color: '#71717a' };
const input = { background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 };
const tableWrap = { background: '#111', borderRadius: 12, overflow: 'hidden', border: '1px solid #27272a' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const thead = { background: '#18181b', borderBottom: '1px solid #27272a' };
const th = { padding: '12px 16px', color: '#71717a', fontWeight: 600, textAlign: 'left' };
const td = { padding: '14px 16px', color: '#fff' };
const rowStyle = { borderBottom: '1px solid #1c1c1c' };
const empty = { padding: 40, textAlign: 'center', color: '#71717a' };
const pill = (c) => ({ background: `${c}20`, color: c, borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 });

export default AdminScreens;
